import React, { useContext } from "react";
import { Box, Typography, Grid } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import defaultPic1 from "../../../assets/images/default_pic_1.png";
import defaultPic2 from "../../../assets/images/default_pic_2.png";
import defaultPic3 from "../../../assets/images/default_pic_3.png";
import { CourseCard } from "../../../components/CourseCard";
import { LatestReleaseBanner } from "../../../components/Library/AllCourses/Home/index";
import ScrollableCourseCards from "../../../components/ScrollableCourseCards/ScrollableCourseCards";
import LoadingSpinner from "../../../components/LoadingSpinner/LoadingSpinner";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import UserAssignmentContext from "../../../context/UserAssignmentContext";
import CourseActionButton from "../../../components/Buttons/CourseActionButton";
import { assignmentTypes } from "../../../constants";

const useStyles = makeStyles(theme => ({
  header: {
    paddingBottom: theme.spacing(1)
  },
  twinColumnsContainer: {
    justifyContent: "space-between"
  },
  twinColumn: {
    flex: "1 1 auto",
    marginRight: 10,
    marginLeft: 10,
    "&:last-child": { marginRight: 0 },
    "&:first-child": { marginLeft: 0 }
  }
}));

const defaultPictures = [defaultPic1, defaultPic2, defaultPic3];

const SuggestedCourses = ({ courses, title, subtitle }) => {
  const classes = useStyles();
  let coursesSetSize = 3;

  const {
    onStartCourse,
    onCreateUserAssignment,
    onResumeAssignment,
    isStarted
  } = useContext(UserAssignmentContext);

  if (!courses || courses.length === 0) {
    return null;
  }
  const courseSets = [];
  for (let i = 0; i < courses.length; i += coursesSetSize) {
    courseSets.push(courses.slice(i, i + coursesSetSize));
  }

  return (
    <Grid data-qa="suggested-courses" item xs={12} md={12}>
      <Typography variant="h3" className={classes.header} color="textSecondary">
        {title}
      </Typography>
      <Typography variant="h6" color="textSecondary">
        {subtitle}
      </Typography>
      <Grid container className={classes.twinColumnsContainer}>
        {courseSets.map((courseSet, ind) => (
          <Box
            display="flex"
            flexDirection="column"
            mb={5}
            className={classes.twinColumn}
            key={"courseSet-" + ind}
          >
            {courseSet.map((course, index) => {
              const userAssignment = course.userAssignment;
              return (
                <Box mt={{ xs: 3, sm: 0 }} key={"card-" + index}>
                  <CourseCard
                    condensed
                    buttons={[
                      <CourseActionButton
                        isEnrolled={!!userAssignment?.enrolledCtoaId}
                        isAssigned={!!userAssignment}
                        isStarted={isStarted(userAssignment)}
                        courseType={course.courseType}
                        onEnrollClick={() => onStartCourse(course)}
                        onResumeCourseClick={() =>
                          onResumeAssignment(userAssignment)
                        }
                        onAddToCoursesClick={() =>
                          onCreateUserAssignment(course)
                        }
                        isInstructorForCourse={course.isInstructorForCourse}
                        prereqs={course.prerequisites || []}
                        key="course-action-button"
                        startDate={course.zonedStartDate}
                        endDate={course.zonedEndDate}
                        iltClassType={course.typeName}
                      />
                    ]}
                    title={course.name}
                    imageSrc={
                      course.image
                        ? course.image
                        : defaultPictures[Math.floor(Math.random() * 3)]
                    }
                    courseType={course.courseType}
                    typeName={course.typeName}
                    flags={course.flags}
                    hasNote={!!userAssignment}
                    isSelfAssigned={
                      userAssignment?.assignmentType == assignmentTypes.self
                    }
                    courseDescription={course.description}
                  />
                </Box>
              );
            })}
          </Box>
        ))}
      </Grid>
    </Grid>
  );
};

const RecommendedCourses = ({ spinner, courses, subtitle }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  if (spinner) {
    return <LoadingSpinner visible={spinner} />;
  }
  return (
    <div>
      <Box my={7}>
        <Typography
          variant="h3"
          color="textSecondary"
          className={classes.header}
        >
          {t("libraryAllCourses.libraryAwardsBanner.recommendedCourses")}
        </Typography>
        <Typography variant="h6" color="textSecondary">
          {subtitle}
        </Typography>
        <ScrollableCourseCards courses={courses} />
      </Box>
    </div>
  );
};

export default function AllCoursesHome(props) {
  const { t } = useTranslation();
  return (
    <Box display="flex" flexDirection="column">
      <Box mb={7}>
        <LatestReleaseBanner latestReleases={props.latestReleases} />
        <RecommendedCourses
          spinner={props.spinner["recommendedCourses"]}
          courses={props.recommendedCourses}
          subtitle={t("libraryAllCourses.libraryAwardsBanner.positionBased")}
        />
        <SuggestedCourses
          courses={props.suggestedCourses}
          title="Courses You Might Like"
          subtitle="Eligible for CEC"
        />
      </Box>
    </Box>
  );
}

AllCoursesHome.propTypes = {
  /**
   * An array of objects each containing course data to be displayed
   */
  awards: PropTypes.arrayOf(
    PropTypes.shape({
      awardTitle: PropTypes.string,
      description: PropTypes.string,
      color: PropTypes.string,
      shape: PropTypes.string
    })
  ),
  /**
   * An array of objects each containing course data to be displayed
   * in the latest releases banner at the top of the page
   */
  latestReleases: PropTypes.any,
  /**
   * An array of objects containing course data
   * for the horizontally scrollable recommended courses section
   */
  recommendedCourses: PropTypes.any,
  assignedCourses: PropTypes.any,
  spinner: PropTypes.any,
  /**
   * For the "Courses You Might Like" section at the bottom of the page
   */
  suggestedCourses: PropTypes.any // TODO: break this up into two sets of courses, one for each column
};
